import type { MemberCapacity } from '@/domains/calendar/hooks/useTeamCapacities';
import type { Job } from '@/domains/jobs/components/JobCard';
import { format } from 'date-fns';
import { CalendarOff } from 'lucide-react';
import type { GanttZoomLevel } from '../toolbar/types';
import { GanttMemberJobBar } from './GanttMemberJobBar';
import { layoutGanttJobBars } from './layoutJobBars';

const MEMBER_ROW_MIN_HEIGHT = 44;
const MEMBER_LANE_HEIGHT = 32;
const MEMBER_ROW_PADDING = 8;
const NO_JOBS: Job[] = [];

interface GanttMemberSubRowProps {
  member: MemberCapacity;
  rangeStart: Date;
  pixelsPerDay: number;
  totalDays: number;
  /** This member's own bookings for the visible range, drawn as read-only bars. */
  jobs?: Job[];
  zoomLevel?: GanttZoomLevel;
  onJobClick?: (jobId: string) => void;
}

export function GanttMemberSubRow({
  member,
  rangeStart,
  pixelsPerDay,
  totalDays,
  jobs = NO_JOBS,
  zoomLevel = 'week',
  onJobClick,
}: GanttMemberSubRowProps) {
  const { bars, laneCount } = layoutGanttJobBars(jobs, {
    rangeStart,
    pixelsPerDay,
    totalDays,
    zoomLevel,
  });
  const rowHeight = Math.max(
    MEMBER_ROW_MIN_HEIGHT,
    laneCount * MEMBER_LANE_HEIGHT + MEMBER_ROW_PADDING,
  );

  return (
    <div
      data-testid="gantt-member-sub-row"
      data-user-id={member.userId}
      // `isolate` contains the sticky name cell's z-index so the sub-row slides
      // under the sticky header instead of over it (WRK-5717).
      className="flex border-b border-border bg-muted/10 isolate"
      style={{ minHeight: `${rowHeight}px` }}
    >
      {/* Member name (sticky left column, indented to mirror the team row layout) */}
      <div className="w-[200px] flex-shrink-0 border-r-2 border-border px-4 py-2 bg-card/70 flex items-center sticky left-0 z-30">
        <div className="w-full pl-[18px] space-y-1">
          <div className="text-xs font-medium text-foreground truncate" title={member.userName}>
            {member.userName}
          </div>
          {/* Per-user capacity bar intentionally omitted (WRK-5646): capacity is
              shown at the team level only. Keep the on-leave badge so members
              unavailable for the whole range are still flagged. */}
          {member.isOnLeaveAllRange && (
            <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <CalendarOff className="w-3 h-3" aria-hidden="true" />
              <span>On leave</span>
            </div>
          )}
        </div>
      </div>

      {/* Per-day capacity bars */}
      <div
        className="flex-1 relative overflow-hidden"
        style={{ width: `${totalDays * pixelsPerDay}px`, minHeight: `${rowHeight}px` }}
      >
        {Array.from({ length: totalDays }).map((_, i) => {
          const date = new Date(rangeStart);
          date.setDate(date.getDate() + i);
          const dateStr = format(date, 'yyyy-MM-dd');
          const isWeekend = date.getDay() === 0 || date.getDay() === 6;
          const cap = member.daily.get(dateStr);
          const pct = cap?.utilizationPercent ?? 0;
          const onLeave = cap?.isOnLeave ?? false;

          let barColor = 'bg-green-500/60';
          if (onLeave) barColor = 'bg-zinc-300 dark:bg-zinc-700';
          else if (pct > 100) barColor = 'bg-red-500/70';
          else if (pct >= 90) barColor = 'bg-yellow-500/70';

          return (
            <div
              key={dateStr}
              className={`absolute top-0 bottom-0 border-r border-border/40 ${
                isWeekend ? 'bg-muted/15' : ''
              }`}
              style={{ left: `${i * pixelsPerDay}px`, width: `${pixelsPerDay}px` }}
            >
              {(pct > 0 || onLeave) && (
                <div
                  className={`absolute bottom-0 left-1/2 -translate-x-1/2 ${barColor} rounded-t-sm transition-all`}
                  style={{
                    width: `${Math.min(pixelsPerDay - 8, 40)}px`,
                    height: onLeave ? '4px' : `${Math.min(Math.max(pct, 4), 100) * 0.4}px`,
                    maxHeight: '40px',
                  }}
                  title={
                    onLeave
                      ? `${member.userName}: on leave on ${format(date, 'd MMM')}`
                      : `${member.userName}: ${cap?.bookedHours.toFixed(1)}h / ${cap?.availableHours}h (${pct.toFixed(0)}%)`
                  }
                />
              )}
            </div>
          );
        })}

        {bars.map((bar, index) => (
          <div
            key={`${bar.job.id}-${bar.lane}-${index}`}
            className="absolute z-10"
            style={{
              left: `${bar.left}px`,
              top: `${4 + bar.lane * MEMBER_LANE_HEIGHT}px`,
              width: `${bar.width}px`,
            }}
          >
            <GanttMemberJobBar job={bar.job} width={bar.width} onJobClick={onJobClick} />
          </div>
        ))}
      </div>
    </div>
  );
}
